import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { LiksLucideIcon } from '../../../components/liks/LiksLucideIcon/LiksLucideIcon';
import { useKnowledgeDatabaseQuery } from '../../../features/knowledge/queries/useKnowledgeDatabaseQuery';
import {
  DEFAULT_KNOWLEDGE_DATABASE_TAB,
  resolveKnowledgeDatabaseTabPath,
} from '../../../features/knowledge/knowledgeTabs';
import { useI18n } from '../../../i18n/useI18n';
import type { ChatbotWithIdentity } from '../../../types/chatbot';
import { formatTimestamp } from '../../../utils/formatUtils';
import { normalizeLucideIconName } from '../../../utils/lucideIconCatalog';
import './ChatbotOverviewTab.css';

type ChatbotOverviewTabProps = {
  chatbot: ChatbotWithIdentity;
};

export function ChatbotOverviewTab({ chatbot }: ChatbotOverviewTabProps) {
  const { locale, translate } = useI18n();
  const navigate = useNavigate();
  const knowledgeBaseId = chatbot.knowledgeBaseId?.trim() ?? '';
  const databaseQuery = useKnowledgeDatabaseQuery(
    knowledgeBaseId !== '' ? knowledgeBaseId : undefined,
  );

  if (knowledgeBaseId === '') {
    return (
      <section className="chatbot-overview">
        <p className="chatbot-overview__status">
          {translate('chatbots.overview.knowledgeBase.missing')}
        </p>
      </section>
    );
  }

  if (databaseQuery.isLoading) {
    return (
      <section className="chatbot-overview">
        <p className="chatbot-overview__status">
          {translate('chatbots.overview.knowledgeBase.loading')}
        </p>
      </section>
    );
  }

  const database = databaseQuery.data;
  if (!database || databaseQuery.isError) {
    return (
      <section className="chatbot-overview">
        <h2 className="chatbot-overview__title">
          {translate('chatbots.overview.knowledgeBase.title')}
        </h2>
        <p className="chatbot-overview__status" role="status">
          {translate('chatbots.overview.knowledgeBase.noAccess')}
        </p>
      </section>
    );
  }

  const description = database.description?.trim() ?? '';
  const databaseName =
    database.name?.trim() || translate('chatbots.unknownKnowledgeBase');
  const lastModified = database.lastModified
    ? formatTimestamp(database.lastModified, locale)
    : translate('knowledge.notAvailable');

  return (
    <section className="chatbot-overview">
      <h2 className="chatbot-overview__title">
        {translate('chatbots.overview.knowledgeBase.title')}
      </h2>
      <article className="chatbot-overview__database-card">
        <button
          type="button"
          className="chatbot-overview__database-open"
          aria-label={translate('chatbots.overview.knowledgeBase.open', {
            name: databaseName,
          })}
          onClick={() =>
            navigate(
              resolveKnowledgeDatabaseTabPath(
                knowledgeBaseId,
                DEFAULT_KNOWLEDGE_DATABASE_TAB,
              ),
            )
          }
        >
          <ArrowRight
            className="chatbot-overview__database-open-icon"
            strokeWidth={2}
            aria-hidden
          />
        </button>
        <div className="chatbot-overview__database-heading">
          <span className="chatbot-overview__database-icon" aria-hidden>
            <LiksLucideIcon
              name={normalizeLucideIconName(database.icon)}
              strokeWidth={2}
            />
          </span>
          <span className="chatbot-overview__database-name">
            {databaseName}
          </span>
        </div>
        <p
          className={[
            'chatbot-overview__database-description',
            description === '' &&
              'chatbot-overview__database-description--empty',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {description !== ''
            ? description
            : translate('knowledge.noDescription')}
        </p>
        <div
          className="chatbot-overview__database-notice"
          role="note"
          aria-label={translate('chatbots.createHowItWorksLabel')}
        >
          <p className="chatbot-overview__database-notice-title">
            {translate('chatbots.createHowItWorksTitle')}
          </p>
          <ul className="chatbot-overview__database-notice-list">
            <li>{translate('chatbots.createHowItWorksCurrentContent')}</li>
            <li>{translate('chatbots.createHowItWorksFutureContent')}</li>
          </ul>
        </div>
        <dl className="chatbot-overview__database-stats">
          <div className="chatbot-overview__database-stat">
            <dt>{translate('knowledge.stats.documents')}</dt>
            <dd>{database.documentCount ?? 0}</dd>
          </div>
          <div className="chatbot-overview__database-stat">
            <dt>{translate('knowledge.stats.crawls')}</dt>
            <dd>{database.crawlsCount ?? 0}</dd>
          </div>
          <div className="chatbot-overview__database-stat">
            <dt>{translate('knowledge.stats.members')}</dt>
            <dd>{database.memberCount ?? 0}</dd>
          </div>
          <div className="chatbot-overview__database-stat">
            <dt>{translate('knowledge.stats.lastModified')}</dt>
            <dd>{lastModified}</dd>
          </div>
        </dl>
      </article>
    </section>
  );
}
